import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  useTheme,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { useAuth } from '../hooks/useAuth';
import { getProperties } from '../services/propertyService';
import openScheduleService from '../services/openScheduleService';

interface CalendarAssistantBarProps {
  open: boolean;
  onClose: () => void;
  // Optional: currently selected property to highlight in the matrix
  propertyId?: string;
  // Optional initial date (YYYY-MM-DD) to focus when opening the assistant
  initialDate?: string;
  agencySlug: string;
  onSuggestionSelect?: (suggestion: any) => void;
  // Optional handler for user-friendly warnings (avoids raw window.alert)
  onWarningMessage?: (message: string) => void;
  // Optional: externally selected time range to display/highlight
  currentSelection?: {
    date: string;
    startTime: string;
    endTime: string;
  };
  // Optional: local draft sessions that haven't been saved to backend yet
  localSessions?: Array<{
    date: string;
    startTime: string;
    endTime: string;
  }>;
}

/**
 * CalendarAssistantBar
 *
 * A right-hand helper panel (similar to Zoom's calendar assistant)
 * that shows the best available time slots for all property assignees
 * and lets an agent pick one to use when creating an open schedule.
 */
const CalendarAssistantBar: React.FC<CalendarAssistantBarProps> = ({
  open,
  onClose,
  propertyId,
  initialDate,
  agencySlug,
  onSuggestionSelect,
  onWarningMessage,
  currentSelection,
  localSessions = []
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matrixProperties, setMatrixProperties] = useState<any[]>([]);
  // Map of propertyId -> array of booked open-house sessions (for the selected day)
  const [takenByProperty, setTakenByProperty] = useState<Record<string, any[]>>({});

  // Default window: today -> +30 days
  const { startDate, endDate } = useMemo(() => {
    const today = new Date();
    const end = new Date(today);
    end.setDate(today.getDate() + 7); // Assistant focuses on the next week

    const toISO = (d: Date) => d.toISOString().split('T')[0];

    return {
      startDate: toISO(today),
      endDate: toISO(end)
    };
  }, []);

  // Selected day for the assistant matrix
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // When the assistant opens or the initial date changes, focus that day (fall back to today)
  useEffect(() => {
    if (!open) return;
    if (initialDate) {
      setSelectedDate(initialDate);
    } else {
      setSelectedDate(startDate);
    }
  }, [open, initialDate, startDate]);

  // Generate 15-minute time slots between 9am and 6pm
  const timeSlots = useMemo(() => {
    const slots: { label: string; start: string; end: string }[] = [];
    const startHour = 9;
    const endHour = 18;

    const intervalMinutes = 15;

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += intervalMinutes) {
        const slotStart = new Date(2000, 0, 1, hour, minute);
        const slotEnd = new Date(slotStart.getTime() + intervalMinutes * 60 * 1000);
        const fmt = (d: Date) => d.toTimeString().slice(0, 5);
        slots.push({
          label: slotStart.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }),
          start: fmt(slotStart),
          end: fmt(slotEnd)
        });
      }
    }

    return slots;
  }, []);

  // Helper to format time for display (e.g., "10:00" -> "10:00 AM")
  const formatTimeForDisplay = (timeStr: string): string => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    const hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${minutes} ${period}`;
  };

  // When picking variable-length slots, we allow the user to click once to
  // choose a start time and a second time (on the same or later column) to
  // choose the end time.
  // - pendingRangeStart: first click (start time)
  // - activeRange: last committed range (for visual highlight)
  const [pendingRangeStart, setPendingRangeStart] = useState<{
    start: string;
    label: string;
  } | null>(null);
  const [activeRange, setActiveRange] = useState<{
    start: string;
    end: string;
  } | null>(null);

  // Reset pending/active range when the selected day changes
  useEffect(() => {
    setPendingRangeStart(null);
    setActiveRange(null);
  }, [selectedDate]);

  // Calculate the effective range to display (combining internal interaction and external props)
  const displayRange = useMemo(() => {
    // If user has actively selected a range via clicking in the assistant, prioritize that
    if (activeRange) return activeRange;

    // Otherwise fall back to the external selection (from parent form)
    // We strictly match the date here. The parent form is responsible for updating
    // currentSelection.date if the user switches days in the form (e.g. auto-fill).
    // If the user just navigates the assistant (Prev/Next), we shouldn't show the
    // selection from a different day.
    if (currentSelection && currentSelection.startTime && currentSelection.endTime) {
      const selDateStr = currentSelection.date.split('T')[0];
      const viewDateStr = selectedDate?.split('T')[0];
      
      if (selDateStr === viewDateStr) {
        return {
          start: currentSelection.startTime,
          end: currentSelection.endTime
        };
      }
    }
    return null;
  }, [activeRange, currentSelection, selectedDate]);

  // Load active properties where the current user is an assignee,
  // plus booked open-house sessions for each property on the selected day.
  useEffect(() => {
    if (!open || !agencySlug || !user?.id || !selectedDate) {
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load active properties (similar to Calendar page)
        const propertiesResp: any = await getProperties(agencySlug, 1, 200, false);
        const propertiesList: any[] = Array.isArray(propertiesResp)
          ? propertiesResp
          : propertiesResp?.properties || [];

        const active = propertiesList.filter((p: any) => p.status !== 'archived');

        // Only include properties where the current user is an assignee
        const mine = active.filter((p: any) => {
          const id = user.id;
          return (
            p.primaryAssignee === id ||
            p.secondaryAssignee === id ||
            p.tertiaryAssignee === id
          );
        });

        setMatrixProperties(mine);

        if (mine.length === 0) {
          setTakenByProperty({});
          return;
        }

        // Fetch upcoming open-house sessions across the agency and use them
        // to mark taken slots for the selected day.
        const upcomingResp = await openScheduleService.getUpcomingSessions(
          agencySlug,
          1,
          500
        );
        const upcoming = upcomingResp?.data || [];

        const takenMap: Record<string, any[]> = {};

        // Normalize selected date for comparison (handle both Date and string)
        const selectedDateObj = new Date(selectedDate + 'T00:00:00');
        const selectedDateOnly = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate());

        // Helper to normalize time to "HH:MM" format
        const normalizeTime = (timeStr: string): string => {
          if (!timeStr) return '00:00';
          // Handle formats like "10:00:00", "10:00", "10:0", etc.
          const parts = timeStr.split(':');
          if (parts.length >= 2) {
            const hours = parts[0].padStart(2, '0');
            const minutes = parts[1].padStart(2, '0');
            return `${hours}:${minutes}`;
          }
          return '00:00';
        };

        // Helper to check if two dates are the same day
        const isSameDay = (date1: Date, date2: Date): boolean => {
          return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
          );
        };

        // Add local draft sessions to the list of "upcoming" sessions to process
        // We only care about local sessions for the current property
        if (propertyId && localSessions.length > 0) {
          localSessions.forEach(ls => {
             // Create a fake session object compatible with the upcoming sessions format
             upcoming.push({
               property: { _id: propertyId }, // Only matches current property
               date: ls.date,
               startTime: ls.startTime,
               endTime: ls.endTime,
               isLocalDraft: true // Flag to distinguish if needed
             });
          });
        }

        upcoming.forEach((session: any) => {
          const prop = session.property;
          if (!prop || !prop._id) return;

          const propIdStr = String(prop._id);

          // Only consider properties in our matrix list (where user is assignee)
          if (!mine.some(p => String(p._id) === propIdStr)) return;

          // Match the selected date - handle both Date objects and ISO strings
          let sessionDate: Date;
          if (session.date instanceof Date) {
            sessionDate = new Date(session.date);
          } else if (typeof session.date === 'string') {
            // Handle ISO string or date-only string
            sessionDate = new Date(session.date.includes('T') ? session.date : session.date + 'T00:00:00');
          } else {
            return; // Skip if date is invalid
          }

          if (!isSameDay(sessionDate, selectedDateOnly)) return;

          // Normalize start and end times as "HH:MM"
          const startTime = normalizeTime(session.startTime || '00:00');
          const endTime = normalizeTime(session.endTime || startTime);

          if (!takenMap[propIdStr]) {
            takenMap[propIdStr] = [];
          }
          takenMap[propIdStr].push({
            startTime,
            endTime,
            title: session.property?.address
              ? `${session.property.address.street || ''} ${session.property.address.city || ''}`.trim() || 'Open House'
              : 'Open House',
            isLocalDraft: session.isLocalDraft
          });
        });

        setTakenByProperty(takenMap);
      } catch (e: any) {
        setError(e?.message || 'Failed to load assistant data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, agencySlug, user?.id, selectedDate]);

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        backgroundColor: theme.palette.background.paper
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTimeIcon color="primary" />
          <Typography variant="h6">
            Calendar Assistant
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {selectedDate && (
            <Typography variant="body2" color="text.secondary">
              {new Date(selectedDate).toLocaleDateString('en-AU', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (!selectedDate) return;
              const d = new Date(selectedDate);
              d.setDate(d.getDate() - 1);
              setSelectedDate(d.toISOString().split('T')[0]);
            }}
          >
            Prev
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const todayStr = new Date().toISOString().split('T')[0];
              setSelectedDate(todayStr);
            }}
          >
            Today
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (!selectedDate) return;
              const d = new Date(selectedDate);
              d.setDate(d.getDate() + 1);
              setSelectedDate(d.toISOString().split('T')[0]);
            }}
          >
            Next
          </Button>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ p: 2, pt: 1, flex: 1, overflow: 'auto' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Assistant is showing available 15-minute slots for your active listings today. 
          Columns are time, rows are properties. Hover a slot to see whether it will be
          used as the start or end time. Click once to choose a start time, then click
          a later time to choose the end time for that opening.
        </Typography>

        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" py={4}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body2">Loading availability…</Typography>
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : matrixProperties.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No active listings assigned to you for today.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: 200,
                      position: 'sticky',
                      left: 0,
                      zIndex: theme.zIndex.appBar,
                      backgroundColor: theme.palette.background.paper
                    }}
                  >
                    Property
                  </TableCell>
                  {timeSlots.map(slot => {
                    // Helper to check if a slot overlaps with a session
                    const slotOverlapsSession = (slotStart: string, slotEnd: string, sessionStart: string, sessionEnd: string): boolean => {
                      const timeToMinutes = (timeStr: string): number => {
                        const parts = timeStr.split(':');
                        if (parts.length < 2) return 0;
                        return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
                      };
                      const slotStartMin = timeToMinutes(slotStart);
                      const slotEndMin = timeToMinutes(slotEnd);
                      const sessionStartMin = timeToMinutes(sessionStart);
                      const sessionEndMin = timeToMinutes(sessionEnd);
                      return slotStartMin < sessionEndMin && slotEndMin > sessionStartMin;
                    };

                    // Find the actual session that overlaps with this slot (if any)
                    let blockingSession: any = null;
                    let draftSession: any = null;
                    
                    Object.values(takenByProperty).forEach(list => {
                      (list || []).forEach((s: any) => {
                        if (s && s.startTime && s.endTime && slotOverlapsSession(slot.start, slot.end, s.startTime, s.endTime)) {
                          if (s.isLocalDraft) {
                            draftSession = s;
                          } else {
                            blockingSession = s;
                          }
                        }
                      });
                    });

                    const overlappingSession = blockingSession || draftSession;
                    const takenAnywhere = overlappingSession !== null;
                    // Check if the overlapping session is a local draft (which we consider "available" in the sense that it's part of the current schedule being built)
                    // If we have a blocking session, isDraft is false (even if we also have a draft session)
                    const isDraft = blockingSession ? false : (draftSession ? true : false);

                    // Highlight the committed selection range in the header for feedback
                    const inActiveRange =
                      !!displayRange &&
                      (() => {
                        const toMinutes = (t: string) =>
                          parseInt(t.slice(0, 2), 10) * 60 +
                          parseInt(t.slice(3, 5), 10);
                        const startM = toMinutes(displayRange.start);
                        const endM = toMinutes(displayRange.end);
                        const slotM = toMinutes(slot.start);
                        return slotM >= startM && slotM < endM;
                      })();

                    const isPendingStart =
                      !!pendingRangeStart && pendingRangeStart.start === slot.start;

                    const headerBg = takenAnywhere && !isDraft
                      ? theme.palette.error.light
                      : isPendingStart
                      ? theme.palette.info.light
                      : inActiveRange
                      ? theme.palette.success.light
                      : isDraft
                      ? theme.palette.info.light
                      : 'transparent';

                    let headerTitle: string;
                    if (takenAnywhere && overlappingSession && !isDraft) {
                      const sessionStart = formatTimeForDisplay(overlappingSession.startTime);
                      const sessionEnd = formatTimeForDisplay(overlappingSession.endTime);
                      headerTitle = `This time slot (${slot.label}) is already taken by one of your active listings. The taken slot spans from ${sessionStart} to ${sessionEnd}.`;
                    } else if (isDraft) {
                      headerTitle = `This slot is scheduled in your current draft.`;
                    } else if (!pendingRangeStart) {
                      headerTitle = `Click to use ${slot.label} as the START time.`;
                    } else if (isPendingStart) {
                      headerTitle = `${slot.label} is selected as the START time. Click a later slot to choose the END time.`;
                    } else {
                      headerTitle = `Click to use ${slot.label} as the END time (start is ${pendingRangeStart.label}).`;
                    }

                    const canPickFromHeader =
                      Boolean(onSuggestionSelect) &&
                      Boolean(propertyId) &&
                      Boolean(selectedDate);

                    return (
                      <TableCell
                        key={slot.start}
                        align="center"
                        sx={{
                          minWidth: 80,
                          fontSize: '0.75rem',
                          cursor: canPickFromHeader ? 'pointer' : 'default',
                          backgroundColor: headerBg,
                          '&:hover': canPickFromHeader && !takenAnywhere
                            ? {
                                backgroundColor: theme.palette.success.main,
                                color: '#fff'
                              }
                            : undefined
                        }}
                        onClick={() => {
                          if (!selectedDate || !canPickFromHeader) return;
                          if (takenAnywhere) {
                            const message = `You already have an open house scheduled at ${slot.label} on ${new Date(
                              selectedDate
                            ).toLocaleDateString('en-AU', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}. Please choose a different time.`;

                            if (onWarningMessage) {
                              onWarningMessage(message);
                            } else {
                              // Fallback for contexts without custom handler
                              window.alert(message);
                            }
                            return;
                          }
                          if (!onSuggestionSelect) return;

                          // First click: remember this as the range start
                          if (!pendingRangeStart) {
                            setPendingRangeStart({ start: slot.start, label: slot.label });
                            // Clear any previous committed range so we don't confuse the user
                            setActiveRange(null);
                            return;
                          }

                          // Second click: use the earlier boundary as start, later as end.
                          // We treat each column's START time as the boundary, so a range
                          // from 9:15 to 9:45 is represented as [9:15, 9:45).
                          const toMinutes = (t: string) =>
                            parseInt(t.slice(0, 2), 10) * 60 +
                            parseInt(t.slice(3, 5), 10);
                          const startMinutes = toMinutes(pendingRangeStart.start);
                          const endMinutes = toMinutes(slot.start);

                          const finalStart =
                            endMinutes >= startMinutes ? pendingRangeStart.start : slot.start;
                          const finalEnd =
                            endMinutes >= startMinutes ? slot.start : pendingRangeStart.start;

                          // Visually highlight the committed range (entire span between
                          // start and end will be shaded in the header)
                          setActiveRange({ start: finalStart, end: finalEnd });

                          onSuggestionSelect({
                            date: selectedDate,
                            startTime: finalStart,
                            endTime: finalEnd,
                            status: 'available',
                            propertyId
                          });

                          // Reset pending range for the next selection
                          setPendingRangeStart(null);
                        }}
                      >
                        <Tooltip title={headerTitle}>
                          <span>{slot.label}</span>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Helper to convert "HH:MM" to minutes since midnight
                  const timeToMinutes = (timeStr: string): number => {
                    const parts = timeStr.split(':');
                    if (parts.length < 2) return 0;
                    return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
                  };

                  // Helper to check if a slot overlaps with a session
                  // A slot overlaps if: slotStart < sessionEnd && slotEnd > sessionStart
                  const slotOverlapsSession = (slotStart: string, slotEnd: string, sessionStart: string, sessionEnd: string): boolean => {
                    const slotStartMin = timeToMinutes(slotStart);
                    const slotEndMin = timeToMinutes(slotEnd);
                    const sessionStartMin = timeToMinutes(sessionStart);
                    const sessionEndMin = timeToMinutes(sessionEnd);
                    return slotStartMin < sessionEndMin && slotEndMin > sessionStartMin;
                  };

                  // Build a set of all slot start times that are taken by any property
                  // (checking for overlaps, not just exact start time matches)
                  const globallyTakenTimes = new Set<string>();
                  Object.values(takenByProperty).forEach(list => {
                    (list || []).forEach((s: any) => {
                      // Exclude local drafts from global taken check - we handle them via isTakenHere/isDraftHere
                      if (s && s.startTime && s.endTime && !s.isLocalDraft) {
                        // Mark all slots that overlap with this session
                        timeSlots.forEach(slot => {
                          if (slotOverlapsSession(slot.start, slot.end, s.startTime, s.endTime)) {
                            globallyTakenTimes.add(slot.start);
                          }
                        });
                      }
                    });
                  });

                  return matrixProperties.map((p: any) => {
                    const propIdStr = String(p._id);
                    const takenSessions = takenByProperty[propIdStr] || [];

                    const displayAddress =
                      typeof p.address === 'string'
                        ? p.address
                        : `${p.address?.street || ''} ${p.address?.city || ''}`.trim();

                    const isHighlighted =
                      Boolean(propertyId) &&
                      Boolean(p._id) &&
                      p._id.toString() === propertyId;
                    const isCurrentPropertyRow = Boolean(isHighlighted);

                    return (
                      <TableRow key={p._id} hover>
                        <TableCell
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: isHighlighted ? 'bold' : 'normal',
                            whiteSpace: 'nowrap',
                            position: 'sticky',
                            left: 0,
                            zIndex: theme.zIndex.appBar - 1,
                            backgroundColor: theme.palette.background.paper
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <HomeWorkIcon
                              fontSize="small"
                              color={isHighlighted ? 'primary' : 'action'}
                            />
                            <Tooltip title={displayAddress || 'Property'}>
                              <span>
                                {displayAddress || 'Property'}
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {timeSlots.map(slot => {
                          // Check if this property has a session that overlaps with this slot
                          const matching = takenSessions.filter((s: any) =>
                            s && s.startTime && s.endTime && slotOverlapsSession(slot.start, slot.end, s.startTime, s.endTime)
                          );

                          const isTakenHere = matching.length > 0;
                          const overlappingSession = matching.length > 0 ? matching[0] : null;
                          const isDraftHere = overlappingSession?.isLocalDraft;
                          
                          const isTakenAnywhere = globallyTakenTimes.has(slot.start);

                          // A slot is "available" (✓) for this property only if:
                          // - this property does NOT already have a session at that time (unless it's a draft), and
                          // - NO other property has a session at that time either.
                          const isAvailable = (!isTakenHere || isDraftHere) && !isTakenAnywhere;

                          // Visual styling:
                          // - Red background where this property has a session (unless draft)
                          // - Amber/orange background where another property has taken the slot
                          // - Blue background if it's a draft or currently selected range
                          // - Default background otherwise
                          
                          // Check if this slot is part of the currently selected range (via header highlight)
                          // We reuse the inActiveRange logic calculated for the header, but we need to recalculate or pass it
                          const isSelectedRange = (() => {
                             if (!displayRange) return false;
                             const toMinutes = (t: string) =>
                               parseInt(t.slice(0, 2), 10) * 60 +
                               parseInt(t.slice(3, 5), 10);
                             const startM = toMinutes(displayRange.start);
                             const endM = toMinutes(displayRange.end);
                             const slotM = toMinutes(slot.start);
                             return slotM >= startM && slotM < endM;
                          })();

                          const bgColor = isTakenHere && !isDraftHere
                            ? theme.palette.error.light
                            : isTakenAnywhere
                            ? theme.palette.warning.light
                            : (isDraftHere || (isSelectedRange && isCurrentPropertyRow))
                            ? theme.palette.info.light
                            : 'transparent';

                          // Find the overlapping session to get its time span
                          // const overlappingSession = matching.length > 0 ? matching[0] : null; // Already declared above
                          const overlappingSessionAnywhere = isTakenAnywhere && !isTakenHere
                            ? (() => {
                                // Find which session from any property overlaps with this slot
                                for (const list of Object.values(takenByProperty)) {
                                  for (const s of list || []) {
                                    if (s && s.startTime && s.endTime && slotOverlapsSession(slot.start, slot.end, s.startTime, s.endTime)) {
                                      return s;
                                    }
                                  }
                                }
                                return null;
                              })()
                            : null;

                          const baseTimeText = `${slot.label} on ${
                            selectedDate &&
                            new Date(selectedDate).toLocaleDateString('en-AU', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })
                          }`;

                          let title: string;
                          if (!isCurrentPropertyRow) {
                            if (isTakenHere && overlappingSession) {
                              const sessionStart = formatTimeForDisplay(overlappingSession.startTime);
                              const sessionEnd = formatTimeForDisplay(overlappingSession.endTime);
                              title = `This property already has an open house at this time.\nThe taken slot spans from ${sessionStart} to ${sessionEnd}.\n${baseTimeText}`;
                            } else if (isTakenAnywhere && overlappingSessionAnywhere) {
                              const sessionStart = formatTimeForDisplay(overlappingSessionAnywhere.startTime);
                              const sessionEnd = formatTimeForDisplay(overlappingSessionAnywhere.endTime);
                              title = `Another one of your properties already has an open at this time.\nThe taken slot spans from ${sessionStart} to ${sessionEnd}.\n${baseTimeText}`;
                            } else {
                              title = `You can only pick times for the property you are currently viewing.\n${baseTimeText}`;
                            }
                          } else {
                            if (isTakenHere && overlappingSession) {
                              const sessionStart = formatTimeForDisplay(overlappingSession.startTime);
                              const sessionEnd = formatTimeForDisplay(overlappingSession.endTime);
                              title = `Open house already scheduled for this property and time.\nThe taken slot spans from ${sessionStart} to ${sessionEnd}.\n${baseTimeText}`;
                            } else if (!isAvailable && overlappingSessionAnywhere) {
                              const sessionStart = formatTimeForDisplay(overlappingSessionAnywhere.startTime);
                              const sessionEnd = formatTimeForDisplay(overlappingSessionAnywhere.endTime);
                              title = `Another property already has an open at this time.\nThe taken slot spans from ${sessionStart} to ${sessionEnd}.\n${baseTimeText}`;
                            } else {
                              title = `No open house scheduled for this slot.\n${baseTimeText}`;
                            }
                          }

                          // Determine what symbol to show
                          const cellSymbol = isTakenHere && !isDraftHere
                            ? '×' // this property has the session
                            : isTakenAnywhere
                            ? '×' // another property has taken this slot
                            : (isAvailable || isDraftHere) && isCurrentPropertyRow
                            ? '✓' // only show ✓ for the current property when truly free or draft
                            : '';

                          return (
                            <TableCell
                              key={slot.start}
                              align="center"
                              sx={{
                                cursor: isTakenAnywhere || (isTakenHere && !isDraftHere) ? 'not-allowed' : 'default',
                                backgroundColor: bgColor,
                                transition: 'background-color 0.15s ease-in-out',
                                fontSize: '0.9rem',
                                fontWeight: cellSymbol === '×' ? 'bold' : 'normal',
                                color: cellSymbol === '×' ? theme.palette.error.main : 'inherit',
                                p: 0.5,
                                border: (isTakenHere && !isDraftHere) || isTakenAnywhere ? `1px solid ${theme.palette.divider}` : 'none'
                              }}
                            >
                              <Tooltip title={title}>
                                <span style={{ display: 'inline-block', minWidth: '20px' }}>
                                  {cellSymbol}
                                </span>
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default CalendarAssistantBar;


